import {
  useState, useMemo,
} from 'react';
import { useTranslation } from 'next-i18next';
import { CustomPillTopBarProps } from '@type/filters';
import {
  Chip, ListboxStyled, ListboxStyledButton, ListboxStyledOptions, ListboxStyledOption,
} from '@components/common';
import { Button, Input } from '@components/common/Forms';
import { XMarkIcon } from '@heroicons/react/16/solid';
import { useLocationsBuilder } from '@hooks/form-helpers/useLocationsBuilder';
import { filterStyles } from '../helpers/filterStyles';

/**
 * @description The top bar locations filter component.
 * ***submitFunc** - the function to trigger the form submit.
 * ***recordFunc** - the 'record' function from useBjForm.
 * ***clearGroup** - the function to clear the group. Defaults to undefined.
 * ***idPrefix** - the prefix for the form elements ids. When using multiple forms on the same page,
 * this is needed to avoid duplicate ids.
 * ***queryParams** - the query params object.
 */
export const TopBarLocations = (props: CustomPillTopBarProps) => {
  // Destructure props
  const {
    submitFunc,
    recordFunc,
    clearGroup,
    idPrefix,
    queryParams,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // show/hide the component
  const [hidden, setHidden] = useState(false);

  // Use Locations Builder Hook
  const {
    selectedLocations,
    selectedLocationsString,
    buildSelectedLocationsFromString,
    deleteLocation,
  } = useLocationsBuilder();

  // Handle the clear group
  const clearFilterGroup = () => {
    setHidden(true);
    if (clearGroup) clearGroup();
    if (submitFunc) setTimeout(() => submitFunc(), 50);
  };

  // Handle the delete language
  const removeLocation = (location: string) => {
    deleteLocation(location);
    if (submitFunc) setTimeout(() => submitFunc(), 50);
  };

  // Helper variables
  // **********************************************
  const buttonLabel = useMemo(() => {
    let label = selectedLocations.length > 1
      ? `${t('global.label.locations')}: ${selectedLocations.length} ${t('label.selected.multiple')}`
      : selectedLocations[0];
    label += queryParams?.radiusInKm ? `, +${queryParams?.radiusInKm} km` : '';

    return label;
  }, [t, selectedLocations, queryParams?.radiusInKm]);

  // Render component
  // **********************************************
  return (
    <>
      <ListboxStyled
        className={`relative${hidden || selectedLocations.length === 0 ? ' hidden' : ''}`}
        value={selectedLocations}
        by="name"
        multiple
      >
        {({ open }) => (
          <>
            <ListboxStyledButton
              as={Chip}
              onDelete={clearFilterGroup}
              isDropdownOpen={open}
              withDropdownArrow
              dropdownArrowPosition="left"
            >
              {buttonLabel}
            </ListboxStyledButton>
            <ListboxStyledOptions
              className={`${filterStyles.listboxOptionsWrapper} py-2`}
              animation="slideDown"
              anchor="bottom start"
            >
              { /* List of selected locations */
              selectedLocations.map((location) => (
                <ListboxStyledOption
                  key={location}
                  value={location}
                  className={`${filterStyles.listboxOption} justify-between !py-1 !pl-3`}
                >
                  {location}
                  <Button
                    styling="none"
                    className="group py-1 pl-2.5"
                    onClick={() => removeLocation(location)}
                  >
                    <span className={filterStyles.closeButtonIcon}>
                      <XMarkIcon className="size-4" />
                    </span>
                  </Button>
                </ListboxStyledOption>
              ))
            }
            </ListboxStyledOptions>

          </>
        )}
      </ListboxStyled>


      {/* Input that collects the locations */}
      <Input
        type="hidden"
        value={selectedLocationsString}
        onInput={(e) => {
          // If the event is not trusted (e.isTrusted = false), it means it was triggered programmatically
          // (ie: bjForm's setFormValues() function)
          if (!e.isTrusted) buildSelectedLocationsFromString(e);
        }}
        {...recordFunc('location', {
          id: `${idPrefix || ''}location`,
          convertValue: 'commaStringToArray',
        })}
      />

      {/* Radius filter */}
      <Input type="hidden" {...recordFunc('radiusInKm', { id: `${idPrefix}radiusInKm`, defaultValue: queryParams?.radiusInKm })} />
    </>
  );
};
